import { CitationData } from 'src/citation-generator/types';
import {
  ResourceFromSearchAPIResponse,
  SearchAPIRequest,
  SearchAPIResponse,
  TopicAndStatsAPIResponse,
} from './api';
import { AsyncStatusEnum, SearchCategories } from './common';

export enum ResearcherTabEnum {
  RESULTS = 'Results',
  MASTER_TOPICS = 'All Topics',
  STATISTICS = 'Statistics',
}

export enum SearchResultTabEnum {
  OVERVIEW = 'Full Article',
  HIGHLIGHTS = 'Highlights',
  TOPICS = 'Topics',
}

export enum SearchResultVoteEnum {
  LIKE = 'like',
  DISLIKE = 'dislike',
  NONE = 'none',
}

/** RESEARCH DATA TYPES */

/**
 * Master Topic is topic by topic mapping with resource the topic came from
 * topic map to id of resource
 */
export interface MasterTopics {
  /**
   * topic -> maps to ids[]
   */
  [topic: string]: string[];
}

/**
 * For the contents[] received in API we map sections like:
 * contents = [h1, p, h2, p, p]
 * section1 = [h1,p] and section2 = [h2, p, p] these sections are called Article-outlines
 * */
export interface ArticleOutline {
  heading: string;
  paragraph: string[];
}
/**
 * Topics in API are after contents[] was analysed by AI model
 * thus it need to be mapped to original contents[]
 */
export interface TopicAndContents {
  // topic -> paragraphs
  [topic: string]: string[];
}

/**
 * Current search result
 * id: UI Card for resource
 */
export interface ResearchResults {
  [id: string]: ResultCardState;
}

/**
 * Research Tab context the overall state of the research tab
 */
export interface ResearchTabContext {
  status: AsyncStatusEnum;
  // tab: ResearcherTabEnum;
  activeTab: ResearcherTabEnum;
  // search query
  type: SearchCategories;
  query: string;
  page: number;
  total: number;
  searchTriggeredFrom: 'searchBox' | 'contextMenu';
  // Pulled source count
  results: ResearchResults;
  pulledSourceCount: number;
  resultsCount: number;
  // master topics
  masterTopics: MasterTopics;
  selectedMasterTopic: string;
}

/**
 * The TopicsAndStats api response is used to make
 * CompiledObject which basically contains article
 * outline and topics-content maps
 */
export interface CompiledObject extends TopicAndStatsAPIResponse {
  articleOutlines: ArticleOutline[];
  topicAndContents: TopicAndContents;
}
/**
 * This is final state of UI card for a resource
 * citationData is generated from the API response metaInfo
 */
export interface ResultCardState extends ResourceFromSearchAPIResponse {
  // citation-data and compiled-object
  // are only available after status === RESOLVE
  citationData?: CitationData;
  compiledObject?: CompiledObject;
  // following can be derived from
  activeTab: SearchResultTabEnum;
  vote: SearchResultVoteEnum;
  // this will take care of isDownloading (on pull resource button click)
  status: AsyncStatusEnum;
  genre: SearchCategories;
  // search-engine position
  position: number;
}

export enum ResearchActionNameEnum {
  /** switch between the tabs inside research_manifold_tab */
  SetActiveTab = 'rshr/user/set_active_tab',
  /** Launches a new search i.e total = 0, results => empty
   *  This is called when the user clicks on the search button
   * or press enter or clicks on the search icon or context menu search
   * This is treated as new search and thus old data is lost, hence the name
   * you can give your page-no and type as well to launch a new search for that query
   */
  LaunchNewSearch = 'rshr/user/launch_new_search',
  /**
   * This is called when user scrolls to next page hence load data on next page
   */
  LoadNextPage = 'rshr/user/load_next_page',
  /**
   * User starts searching with LaunchNewSearch or LoadNextPage
   * there response success to give data which is then used to update the state
   * We update results state with the new data.
   */
  SearchSuccess = 'rshr/user/search_success',
  SearchError = 'rshr/user/search_error',
  /**
   * User selected a master topic in topics card
   */
  SetSelectedMasterTopic = 'rshr/user/results/selected_master_topic',
  /**
   * Pulling a resource from click on resource card
   */
  PullingSource = 'rshr/user/pulling_source',
  /**
   * When resource is pulled we should get metaInfo, contents, topics, stats...
   * We perform serval calculations and update the state
   */
  PulledSource = 'rshr/user/result/resource_pulled',
  /**
   * When resource pulling failed
   */
  PulledSourceError = 'rshr/user/pulled_source_error',
  /**
   * When user clicks like button on search card
   */
  VotedResource = 'rshr/user/vote',
  /**
   * Reset store as and when required
   */
  ResetStore = 'rshr/system/reset_store',
  ChangeTabInsideCard = 'rshr/user/change_tab_inside_card',

  /**
   * Set the default search query -- for onboarding step
   */
  SetDefaultSearchQuery = 'rshr/system/set_default_search_query',
}

export interface ResetToInitStateOfResearchState {
  type: ResearchActionNameEnum.ResetStore;
}

export interface SetActiveTabAction {
  type: ResearchActionNameEnum.SetActiveTab;
  payload: {
    activeTab: ResearcherTabEnum;
  };
}

export type LaunchNewSearchPayload = Partial<SearchAPIRequest> & {
  query: string;
  searchTriggeredFrom: 'searchBox' | 'contextMenu';
};

export interface LaunchNewSearchAction {
  type: ResearchActionNameEnum.LaunchNewSearch;
  payload: LaunchNewSearchPayload;
}
export interface LoadNextPageAction {
  type: ResearchActionNameEnum.LoadNextPage;
}

export interface SearchSuccessAction {
  type: ResearchActionNameEnum.SearchSuccess;
  payload: SearchAPIResponse;
}

export interface SearchErrorAction {
  type: ResearchActionNameEnum.SearchError;
}

export interface SetSelectedMasterTopicAction {
  type: ResearchActionNameEnum.SetSelectedMasterTopic;
  payload: {
    selectedMasterTopic: string;
  };
}

export interface PullingSourceAction {
  type: ResearchActionNameEnum.PullingSource;
  payload: {
    id: string;
  };
}

export interface PulledSourceSuccessAction {
  type: ResearchActionNameEnum.PulledSource;
  payload: {
    id: string;
  } & TopicAndStatsAPIResponse;
}

export interface PulledSourceErrorAction {
  type: ResearchActionNameEnum.PulledSourceError;
  payload: {
    id: string;
  };
}

export interface SetActiveResearchTabAction {
  type: ResearchActionNameEnum.SetActiveTab;
  payload: {
    activeTab: ResearcherTabEnum;
  };
}

export interface VotedResourceAction {
  type: ResearchActionNameEnum.VotedResource;
  payload: { id: string; vote: SearchResultVoteEnum };
}

export interface ChangeTabInsideCardAction {
  type: ResearchActionNameEnum.ChangeTabInsideCard;
  payload: {
    tab: SearchResultTabEnum;
    id: string;
  };
}

export interface SetDefaultSearchQueryAction {
  type: ResearchActionNameEnum.SetDefaultSearchQuery;
  payload: {
    query: string;
  };
}

export type ResearchActionTypes =
  | SetActiveTabAction
  | LaunchNewSearchAction
  | LoadNextPageAction
  | SearchSuccessAction
  | SearchErrorAction
  | SetSelectedMasterTopicAction
  | PullingSourceAction
  | PulledSourceSuccessAction
  | PulledSourceErrorAction
  | VotedResourceAction
  | ChangeTabInsideCardAction
  | ResetToInitStateOfResearchState
  | SetDefaultSearchQueryAction;

export const RESEARCH_SUB_TAB_SEQ = [
  ResearcherTabEnum.RESULTS,
  ResearcherTabEnum.MASTER_TOPICS,
  ResearcherTabEnum.STATISTICS,
];

export const RESULT_CARD_TAB_SEQ = [
  SearchResultTabEnum.OVERVIEW,
  SearchResultTabEnum.HIGHLIGHTS,
  SearchResultTabEnum.TOPICS,
];
